import { usePage } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { BankSyncProgress } from "@/components/bank-sync-progress"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import type { BankSync } from "@/types"

function storageKey(bankSync: BankSync) {
  return `bank-import:${bankSync.id}`
}

function summary(bankSync: BankSync) {
  if (bankSync.status === "succeeded") return "Complete"
  if (bankSync.status === "failed") return "Needs attention"
  return "Importing…"
}

/** Progress of the first import after connecting a bank. Stays until the user acknowledges it. */
export function BankSyncPopup() {
  const bankSync = usePage().props.initial_bank_sync
  // Keyed by sync so a new import opens again.
  const [minimizedSyncId, setMinimizedSyncId] = useState<number | null>(() =>
    bankSync && sessionStorage.getItem(storageKey(bankSync)) === "minimized" ? bankSync.id : null,
  )

  if (!bankSync) return null

  const minimized = minimizedSyncId === bankSync.id

  const setMinimized = (nextMinimized: boolean) => {
    if (nextMinimized) sessionStorage.setItem(storageKey(bankSync), "minimized")
    else sessionStorage.removeItem(storageKey(bankSync))
    setMinimizedSyncId(nextMinimized ? bankSync.id : null)
  }

  return (
    <>
      <Dialog open={!minimized} onOpenChange={(open) => setMinimized(!open)}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-xl" showCloseButton={false}>
          <DialogHeader className="flex-row items-start justify-between gap-4">
            <DialogTitle>Bank account import</DialogTitle>
            <Button variant="outline" onClick={() => setMinimized(true)}>
              Minimize
            </Button>
          </DialogHeader>
          <DialogDescription>
            We’re importing your balances and transactions. You can minimize this popup and keep working while the
            import continues.
          </DialogDescription>
          <BankSyncProgress bankSync={bankSync} />
        </DialogContent>
      </Dialog>
      {minimized && (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="fixed bottom-4 left-4 z-50 rounded-xl border bg-card px-5 py-3 text-sm font-semibold shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span aria-live="polite">Bank import: {summary(bankSync)}</span> · Show progress
        </button>
      )}
    </>
  )
}
